// CR151: Hook for managing deal policy configurations
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { 
  DealPolicyConfigRecord, 
  DealPolicyConfig,
  SimulateInput,
  SimulateOutput,
} from "@/lib/deal-policy/types";
import type { Json } from "@/integrations/supabase/types";

// Helper to safely convert DB row to typed record
function toConfigRecord(row: {
  id: string;
  name: string;
  is_active: boolean;
  version: string;
  config_json: Json;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}): DealPolicyConfigRecord {
  return {
    id: row.id,
    name: row.name,
    is_active: row.is_active,
    version: row.version,
    config_json: row.config_json as unknown as DealPolicyConfig,
    created_at: row.created_at,
    updated_at: row.updated_at,
    created_by: row.created_by,
    updated_by: row.updated_by,
  };
}

export function useDealPolicy() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  // Fetch active policy config
  const { data: activeConfig, isLoading, error, refetch } = useQuery({
    queryKey: ["deal-policy-config", "active"],
    queryFn: async (): Promise<DealPolicyConfigRecord | null> => {
      const { data, error } = await supabase
        .from("deal_policy_configs")
        .select("*")
        .eq("is_active", true)
        .maybeSingle();
      
      if (error) throw error;
      return data ? toConfigRecord(data) : null;
    },
  });

  // Fetch all policy configs
  const { data: allConfigs, isLoading: isLoadingAll } = useQuery({
    queryKey: ["deal-policy-config", "all"],
    queryFn: async (): Promise<DealPolicyConfigRecord[]> => {
      const { data, error } = await supabase
        .from("deal_policy_configs")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return (data || []).map(toConfigRecord);
    },
  });

  // Save config mutation
  const saveConfig = useMutation({
    mutationFn: async ({ id, config_json, updated_by }: { 
      id: string; 
      config_json: DealPolicyConfig;
      updated_by: string;
    }) => {
      setIsSaving(true);
      const { error } = await supabase
        .from("deal_policy_configs")
        .update({ 
          config_json: config_json as unknown as Json,
          updated_by,
          updated_at: new Date().toISOString()
        })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deal-policy-config"] });
      toast({ title: "Policy saved", description: "Deal policy configuration updated successfully." });
      setIsSaving(false);
    },
    onError: (err) => {
      toast({ 
        title: "Save failed", 
        description: err instanceof Error ? err.message : "Failed to save policy config",
        variant: "destructive"
      });
      setIsSaving(false);
    },
  });

  // Activate config mutation
  const activateConfig = useMutation({
    mutationFn: async (id: string) => {
      // Deactivate all first
      await supabase
        .from("deal_policy_configs")
        .update({ is_active: false })
        .neq("id", "00000000-0000-0000-0000-000000000000");
      
      // Activate the selected one
      const { error } = await supabase
        .from("deal_policy_configs")
        .update({ is_active: true })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deal-policy-config"] });
      toast({ title: "Config activated", description: "Policy config is now active." });
    },
    onError: (err) => {
      toast({ 
        title: "Activation failed", 
        description: err instanceof Error ? err.message : "Failed to activate config",
        variant: "destructive"
      });
    },
  });

  // Simulate function (runs locally, no backend call needed)
  const simulate = (input: SimulateInput, config: DealPolicyConfig): SimulateOutput => {
    const firedRuleIds: string[] = [];
    const evidenceSnippets: string[] = [];
    let eligibilityScope: SimulateOutput["eligibility_scope"] = "CONSUMER";
    let highestPriority = -1;

    const textToSearch = `${input.headline} ${input.body_snippet || ""}`.toLowerCase();

    // Process eligibility rules by priority (highest first)
    const sortedRules = [...config.eligibility_scope_rules]
      .filter(r => r.enabled)
      .sort((a, b) => b.priority - a.priority);

    for (const rule of sortedRules) {
      // Check source type filter
      if (rule.conditions.applies_to_sources.length > 0 && 
          !rule.conditions.applies_to_sources.includes(input.source_type)) {
        continue;
      }

      // Check threshold condition
      if (rule.conditions.requires_threshold_present) {
        if (!input.threshold_cad || input.threshold_cad < (rule.conditions.min_threshold_cad || 0)) {
          continue;
        }
      } else if (rule.conditions.min_threshold_cad && input.threshold_cad) {
        if (input.threshold_cad < rule.conditions.min_threshold_cad) {
          continue;
        }
      }

      // Check negative keywords first (any match = skip rule)
      if (rule.matchers.negative_keywords.length > 0) {
        const hasNegative = rule.matchers.negative_keywords.some(kw => 
          textToSearch.includes(kw.toLowerCase())
        );
        if (hasNegative) continue;
      }

      // Check keywords
      let keywordsMatched = false;
      let matchedKeyword = "";
      if (rule.matchers.keywords.length > 0) {
        if (rule.matchers.keyword_mode === "ANY") {
          for (const kw of rule.matchers.keywords) {
            if (textToSearch.includes(kw.toLowerCase())) {
              keywordsMatched = true;
              matchedKeyword = kw;
              break;
            }
          }
        } else {
          keywordsMatched = rule.matchers.keywords.every(kw => 
            textToSearch.includes(kw.toLowerCase())
          );
          matchedKeyword = rule.matchers.keywords.join(" + ");
        }
      }

      // Check regex patterns
      let regexMatched = false;
      let matchedPattern = "";
      if (rule.matchers.regex_patterns.length > 0) {
        for (const pattern of rule.matchers.regex_patterns) {
          try {
            const re = new RegExp(pattern, "i");
            const match = re.exec(textToSearch);
            if (match) {
              regexMatched = true;
              matchedPattern = match[0];
              break;
            }
          } catch {
            // Invalid regex, skip
          }
        }
      }

      // Determine if rule fired
      const hasMatchers = rule.matchers.keywords.length > 0 || rule.matchers.regex_patterns.length > 0;
      const matchersPassed = keywordsMatched || regexMatched;
      
      // Rule fires if: (no matchers + threshold met) OR (matchers passed)
      const ruleFired = !hasMatchers ? 
        (rule.conditions.requires_threshold_present && input.threshold_cad && 
         input.threshold_cad >= (rule.conditions.min_threshold_cad || 0)) :
        matchersPassed;

      if (ruleFired && rule.priority > highestPriority) {
        eligibilityScope = rule.scope;
        highestPriority = rule.priority;
        firedRuleIds.push(rule.id);
        if (matchedKeyword) evidenceSnippets.push(`keyword: "${matchedKeyword}"`);
        if (matchedPattern) evidenceSnippets.push(`pattern: "${matchedPattern}"`);
        if (rule.conditions.min_threshold_cad && input.threshold_cad) {
          evidenceSnippets.push(`threshold: $${input.threshold_cad} >= $${rule.conditions.min_threshold_cad}`);
        }
      }
    }

    // Evaluate issues using severity map
    const issues: SimulateOutput["issues"] = [];
    let isPublishable = true;
    let isDedupEligible = true;

    // Example issue checks (would be more comprehensive in real implementation)
    if (!input.headline || input.headline.trim() === "") {
      const issueConfig = config.issue_severity_map["MISSING_VALUE"];
      if (issueConfig) {
        issues.push({
          issue_key: "MISSING_VALUE",
          ...issueConfig
        });
        if (issueConfig.blocks_publish) isPublishable = false;
        if (issueConfig.blocks_dedup) isDedupEligible = false;
      }
    }

    return {
      eligibility_scope: eligibilityScope,
      fired_rule_ids: firedRuleIds,
      evidence_snippets: evidenceSnippets,
      issues,
      is_dedup_eligible: isDedupEligible,
      is_publishable: isPublishable,
    };
  };

  return {
    activeConfig,
    allConfigs,
    isLoading,
    isLoadingAll,
    error,
    isSaving,
    refetch,
    saveConfig: saveConfig.mutate,
    activateConfig: activateConfig.mutate,
    simulate,
  };
}
